// Targeting (decisions 0043, 0059; card engine/docs/composer/cards/combat.md): which enemy an attack turns to, and
// hard lock-on as a mode on top of it.
//
// - `chooseTarget` is the soft auto-target chooser (Genshin's): a pure score over candidates, lowest wins. Its terms
//   are distance, the angle off the attacker's facing, the angle off the stick (when the player is steering) and a
//   priority bonus, each with a weight the game passes in as data. Line of sight is a function the game passes in
//   (the engine's combat never queries a collision world itself); only the best few by score are checked, in order
//   (the auto-target chooser suggestion: score analytic candidates, validate only the top N).
// - `LockOn` is hard lock-on: lock picks with the same chooser, `switchBy` steps to the next target left or right
//   as the camera sees them, `update` drops the lock on death or past a range.
// - `FlickDetector` turns a stream of look deltas (a mouse flick, a pad's right stick pushed and let go, a swipe on
//   a touch screen: all one `look` action) into left and right flicks for `switchBy`.
//
// Conventions: positions are `[x, y, z]` metres; yaw 0 faces +Z and grows towards +X (forward = (sin yaw, cos yaw)
// on the ground plane), the same convention as @rpg/camera. Entity ids are numbers. Nothing here reads a clock or a
// random number, so the same inputs choose the same target in a replay.

/** A point in world space, `[x, y, z]` metres. */
export type TargetPoint = readonly [number, number, number];

/** Who is choosing: where they stand, which way they face (yaw, rad) and, while the player steers, the stick as a
 *  world direction on the ground plane `[x, z]` (its length 0..1 is the tilt). */
export interface TargetFrom {
  pos: TargetPoint;
  facing: number;
  input?: readonly [number, number] | null;
}

/** A target that may be chosen. `priority` raises it (a boss over its adds; the game's numbers). */
export interface TargetCandidate {
  id: number;
  pos: TargetPoint;
  alive: boolean;
  priority?: number;
}

/** The chooser's weights and limits, all from the game. Score = distance·d/range + facing·|Δfacing|/π
 *  + input·|Δstick|/π·tilt − priority·p; the lowest score wins. */
export interface TargetWeights {
  /** Candidates further than this (m, on the ground plane) are never chosen. */
  range: number;
  /** Weight of the distance term (distance as a share of `range`). */
  distance: number;
  /** Weight of the angle between the attacker's facing and the candidate (as a share of π). */
  facing: number;
  /** Weight of the angle between the stick and the candidate (as a share of π), scaled by the tilt. */
  input: number;
  /** Weight of a candidate's priority (subtracted). */
  priority: number;
  /** Stick tilts at or under this are no steering (the input term is left out). */
  inputDead: number;
  /** Candidates more than this far off the facing (rad) are never chosen (π: all round). Default π. */
  maxAngle?: number;
  /** At most this many of the best candidates have their line of sight checked, in score order. Default 6. */
  losChecks?: number;
}

/** One candidate's score and its terms (for a debug overlay, a HUD reticle, a test's report). */
export interface TargetScore {
  id: number;
  score: number;
  distance: number;
  /** Angle off the facing and off the stick (rad; the stick's is 0 when it isn't steering). */
  facingAngle: number;
  inputAngle: number;
  /** Why it was left out, or null when it is in the running. */
  out: "dead" | "range" | "angle" | null;
}

/** Choose-time options. */
export interface ChooseOptions {
  /** Whether `from` can see `to` (the game's collision query). Omitted: everything is visible. */
  lineOfSight?: (from: TargetPoint, to: TargetPoint) => boolean;
  /** Ids never chosen (the attacker itself, a target just released). */
  exclude?: readonly number[];
}

/** Planted defects beside what they break (a game's targeting checks turn them on to prove they can fail). */
export const TARGETING_FAULTS = {
  /** The chooser ignores the facing weight (scored as if it were 0): a layout where facing decides must fail. */
  facingWeightZeroed: false,
};

const TAU = Math.PI * 2;
const wrap = (a: number): number => {
  const r = (((a + Math.PI) % TAU) + TAU) % TAU;
  return r - Math.PI;
};
const yawTo = (dx: number, dz: number): number => (dx === 0 && dz === 0 ? 0 : Math.atan2(dx, dz));

/** Score every candidate (the dead and the out of reach too, marked `out`), in the input's order. */
export function scoreTargets(from: TargetFrom, candidates: readonly TargetCandidate[], w: TargetWeights): TargetScore[] {
  const maxAngle = w.maxAngle ?? Math.PI;
  const facingW = TARGETING_FAULTS.facingWeightZeroed ? 0 : w.facing;
  const stick = from.input ?? null;
  const tilt = stick ? Math.hypot(stick[0], stick[1]) : 0;
  const steering = stick !== null && tilt > w.inputDead;
  const stickYaw = steering ? yawTo(stick[0], stick[1]) : 0;
  return candidates.map((c) => {
    const dx = c.pos[0] - from.pos[0];
    const dz = c.pos[2] - from.pos[2];
    const distance = Math.hypot(dx, dz);
    const bearing = yawTo(dx, dz);
    // a candidate standing on the attacker is dead ahead whichever way it faces
    const facingAngle = distance < 1e-6 ? 0 : Math.abs(wrap(bearing - from.facing));
    const inputAngle = steering && distance >= 1e-6 ? Math.abs(wrap(bearing - stickYaw)) : 0;
    const out: TargetScore["out"] = !c.alive ? "dead" : distance > w.range ? "range" : facingAngle > maxAngle ? "angle" : null;
    const score =
      w.distance * (distance / w.range) + facingW * (facingAngle / Math.PI) + (steering ? w.input * (inputAngle / Math.PI) * Math.min(1, tilt) : 0) - w.priority * (c.priority ?? 0);
    return { id: c.id, score, distance, facingAngle, inputAngle, out };
  });
}

/**
 * The soft auto-target: the in-range, living, visible candidate with the lowest score, or null. Ties go to the
 * lower id, so the choice never depends on the candidates' order. Only the best `losChecks` by score are checked for
 * line of sight; if none of them is visible, nothing is chosen (the attack goes where the attacker faces).
 */
export function chooseTarget(from: TargetFrom, candidates: readonly TargetCandidate[], w: TargetWeights, o: ChooseOptions = {}): number | null {
  const byId = new Map(candidates.map((c) => [c.id, c]));
  const ranked = scoreTargets(from, candidates, w)
    .filter((s) => s.out === null && !o.exclude?.includes(s.id))
    .sort((a, b) => a.score - b.score || a.id - b.id);
  const checks = Math.max(1, w.losChecks ?? 6);
  for (const s of ranked.slice(0, checks)) {
    if (!o.lineOfSight || o.lineOfSight(from.pos, byId.get(s.id)!.pos)) return s.id;
  }
  return null;
}

/** The targeting modes (decision 0043): soft auto-targeting, or hard lock-on as the game's setting allows. */
export type TargetingMode = "soft" | "lock";

/** Lock-on's numbers, from the game. */
export interface LockOnOptions {
  /** How a lock picks its first target (often the soft weights with a longer range). */
  weights: TargetWeights;
  /** A locked target further than this (m) drops the lock. */
  dropRange: number;
  /** Switching looks for the next target within this range (m); default `dropRange`. */
  switchRange?: number;
}

/** Why a lock changed. */
export type LockReason = "locked" | "released" | "switched" | "dead" | "range" | "lost";

/** One change of the lock, as `update`, `toggle` and `switchBy` report it. */
export interface LockChange {
  reason: LockReason;
  from: number | null;
  to: number | null;
}

/** The view a switch is judged in: the camera's yaw (rad, the same convention), so "left" is screen left. */
export interface SwitchView {
  yaw: number;
}

/** Hard lock-on (decision 0059): a toggle that holds one target until it dies, goes out of range or is released, and
 *  steps to the next target left or right as the camera sees them. Plain state, so a game can save it. */
export class LockOn {
  private _target: number | null = null;
  readonly options: LockOnOptions;
  /** Every change, oldest first (the last 32), for a harness. */
  readonly changes: LockChange[] = [];

  constructor(o: LockOnOptions) {
    this.options = o;
  }

  /** The locked target, or null. */
  get target(): number | null {
    return this._target;
  }

  private change(reason: LockReason, to: number | null): LockChange {
    const c: LockChange = { reason, from: this._target, to };
    this._target = to;
    this.changes.push(c);
    if (this.changes.length > 32) this.changes.shift();
    return c;
  }

  /** Lock onto the best target by the lock weights; null (and no change) when there is none. */
  lock(from: TargetFrom, candidates: readonly TargetCandidate[], o: ChooseOptions = {}): LockChange | null {
    const id = chooseTarget(from, candidates, this.options.weights, o);
    return id === null ? null : this.change("locked", id);
  }

  /** Let go of the target. */
  release(reason: Extract<LockReason, "released" | "dead" | "range" | "lost"> = "released"): LockChange | null {
    return this._target === null ? null : this.change(reason, null);
  }

  /** The lock button: lock when free, release when locked. */
  toggle(from: TargetFrom, candidates: readonly TargetCandidate[], o: ChooseOptions = {}): LockChange | null {
    return this._target === null ? this.lock(from, candidates, o) : this.release("released");
  }

  /**
   * Step to the next living target to the screen's left (`dir` < 0) or right (> 0) of the current one, by bearing
   * around the player as the camera at `view.yaw` sees it: the smallest turn in that direction. No wrap: at the end
   * of the row the lock stays. Null when nothing changed.
   */
  switchBy(dir: number, view: SwitchView, from: TargetFrom, candidates: readonly TargetCandidate[], o: ChooseOptions = {}): LockChange | null {
    if (this._target === null || dir === 0) return null;
    const range = this.options.switchRange ?? this.options.dropRange;
    // screen bearing: 0 straight ahead of the camera, positive to the right (the camera's right is (-cos yaw, sin yaw))
    const screen = (p: TargetPoint): number => {
      const dx = p[0] - from.pos[0];
      const dz = p[2] - from.pos[2];
      const fwd = dx * Math.sin(view.yaw) + dz * Math.cos(view.yaw);
      const right = -dx * Math.cos(view.yaw) + dz * Math.sin(view.yaw);
      return Math.atan2(right, fwd);
    };
    const current = candidates.find((c) => c.id === this._target);
    if (!current) return null;
    const a0 = screen(current.pos);
    let best: { id: number; step: number } | null = null;
    for (const c of candidates) {
      if (c.id === this._target || !c.alive || o.exclude?.includes(c.id)) continue;
      if (Math.hypot(c.pos[0] - from.pos[0], c.pos[2] - from.pos[2]) > range) continue;
      const step = (screen(c.pos) - a0) * Math.sign(dir);
      if (step <= 1e-6) continue;
      if (o.lineOfSight && !o.lineOfSight(from.pos, c.pos)) continue;
      if (!best || step < best.step - 1e-9 || (Math.abs(step - best.step) <= 1e-9 && c.id < best.id)) best = { id: c.id, step };
    }
    return best ? this.change("switched", best.id) : null;
  }

  /** Once per step while locked: drop the lock when the target is gone or dead, or past the drop range. */
  update(from: TargetFrom, candidates: readonly TargetCandidate[]): LockChange | null {
    if (this._target === null) return null;
    const t = candidates.find((c) => c.id === this._target);
    if (!t) return this.release("lost");
    if (!t.alive) return this.release("dead");
    if (Math.hypot(t.pos[0] - from.pos[0], t.pos[2] - from.pos[2]) > this.options.dropRange) return this.release("range");
    return null;
  }

  /** The saved state. */
  snapshot(): { target: number | null } {
    return { target: this._target };
  }

  restore(s: { target: number | null }): void {
    this._target = s.target;
  }
}

/** A flick's numbers, from the game. Look deltas are radians (the look action's own units). */
export interface FlickOptions {
  /** A flick: at least this much horizontal turn (rad)… */
  angle: number;
  /** …within this long (s). */
  window: number;
  /** Then the look must stay under this speed (rad/s)… */
  calmSpeed: number;
  /** …for this long (s) before the next flick can fire (a held stick or a long drag fires once). */
  calmTime: number;
}

/**
 * Turns look deltas into flicks: `push(dx, dt)` once per frame with the frame's horizontal look delta; it returns
 * +1 (a flick right), −1 (left) or 0. A mouse flick, a pad's right stick pushed to the side and a swipe on the touch
 * look area are all a burst of turn inside the window, so one detector serves every device.
 */
export class FlickDetector {
  private samples: { t: number; dx: number }[] = [];
  private t = 0;
  private armed = true;
  private calm = 0;
  constructor(readonly options: FlickOptions) {}

  push(dx: number, dt: number): -1 | 0 | 1 {
    const o = this.options;
    this.t += Math.max(0, dt);
    const speed = dt > 0 ? Math.abs(dx) / dt : dx === 0 ? 0 : Infinity;
    if (!this.armed) {
      this.calm = speed < o.calmSpeed ? this.calm + dt : 0;
      if (this.calm >= o.calmTime) {
        this.armed = true;
        this.samples = [];
      }
      return 0;
    }
    if (dx !== 0) this.samples.push({ t: this.t, dx });
    while (this.samples.length && this.samples[0]!.t < this.t - o.window) this.samples.shift();
    const sum = this.samples.reduce((a, s) => a + s.dx, 0);
    if (Math.abs(sum) >= o.angle) {
      this.armed = false;
      this.calm = 0;
      this.samples = [];
      return sum > 0 ? 1 : -1;
    }
    return 0;
  }

  /** Forget everything (the lock was released, a menu opened). */
  reset(): void {
    this.samples = [];
    this.armed = true;
    this.calm = 0;
  }
}
